import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { signOut, type Me } from "../../lib/api";
import { API_URL } from "../../lib/config";
import { clearLocalSession, saveKnockTarget } from "../../lib/draft";
import { qk } from "../../lib/queries";
import { resetShell } from "../../lib/shell";
import { AboutLink } from "./legal-links";

/**
 * The signed-in identity, in the form that identifies the person best: the X
 * handle, which is the door address itself. Never "Signed in as" — the name
 * alone is the whole message.
 *
 * The email fallback is gone (ruling R-0828-3). It existed for the knocker who
 * signed in by email, and there is no such person: X is the only lane, and the
 * one address this product holds is where it WRITES, not who somebody is.
 */
export function whoami(me: Me): string {
  if (me.xHandle) return `@${me.xHandle}`;
  return me.displayName || "signed in";
}

/** What the monogram is keyed on: the handle, or the next most stable thing. */
export function accountKey(me: Me): string {
  return me.xHandle ?? me.displayName;
}

/**
 * Ending the session.
 *
 * VERBATIM the semantics the header has always had, moved rather than rewritten:
 * signing out ends a PERSON, not a request — one fact changing server-side and
 * THREE local stores that carried them (the drafts, the shell, the query cache).
 * All of them go, EVEN IF THE SERVER CALL FAILS: a half-signed-out session still
 * showing somebody's balance is worse than asking them to sign in again.
 *
 * A hook rather than a component, because two chromes offer the same exit — the
 * sidebar's account chip and the mobile account menu — and they must not be two
 * implementations of it.
 */
export function useLeave(): { leave: () => void; leaving: boolean } {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [leaving, setLeaving] = useState(false);

  const leave = () => {
    if (leaving) return;
    setLeaving(true);
    void signOut()
      .catch(() => undefined)
      .finally(() => {
        clearLocalSession();
        resetShell();
        /**
         * WHO is gone is SAID, on the query everyone watches; everything that
         * was theirs is then dropped.
         *
         * The order is the load-bearing part, and it is why this is not one
         * `clear()`. Clearing removes the identity query outright, which leaves
         * every mounted screen holding an observer of a query that no longer
         * exists: it reads `undefined` — "nobody has asked yet" — forever, and
         * a screen already on the page has no reason to re-render and re-ask.
         * That is how a signed-out person kept looking at their own briefing.
         * `null` is the definitive "nobody", written to the LIVE query so the
         * screens hear it.
         */
        queryClient.setQueryData(qk.me(), null);
        queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "me" });
        setLeaving(false);
        void navigate({ to: "/", search: {} });
      });
  };

  return { leave, leaving };
}

/** The way out, as quiet as the identity it ends. A control, never a destination. */
export function SignOutButton({
  leaving,
  onLeave,
  className = "",
}: {
  leaving: boolean;
  onLeave: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onLeave}
      disabled={leaving}
      className={
        "rounded text-xs text-faint transition-colors duration-75 hover:text-text-soft " +
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70 " +
        "focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:opacity-60 " +
        className
      }
    >
      {leaving ? "Signing out" : "Sign out"}
    </button>
  );
}

export const WRONG_ACCOUNT_LINK = "Not you?";

/**
 * The wrong-account escape (approved Part 3b, built here for the first time).
 *
 * Sign-in is sticky, and plenty of people have two X accounts. Somebody who
 * arrives as the wrong one has no way to notice except by reading their own
 * name, and no way out except by hunting for Sign out in a settings panel — so
 * the sentence that states who you are carries the way to stop being them.
 *
 * It is a link inside a sentence, not a button: it must read as an aside, and
 * nobody should be able to hit it on the way to something else.
 *
 * WHAT SURVIVES THE SWEEP, and what does not. `clearLocalSession` wipes the
 * knock draft on purpose — a draft left behind for the next person to sign in
 * on this browser is the clothes-of-the-last-person leak that sweep exists to
 * prevent, and this control cannot know that the next person is the same human.
 * So the private half (the words) goes, and the public half (WHICH DOOR they
 * were at) is put back afterwards, which is what lands them at the right door
 * once they are the right person. They retype the knock. That is the smaller
 * loss.
 */
export function WrongAccount({
  me,
  handle = null,
  legal = false,
  className = "",
}: {
  me: Me;
  /** The door this visit was for, so the trip back lands on it again. */
  handle?: string | null;
  /**
   * THE CONSOLE'S ONLY ROAD TO THE TERMS (owner, 2026-09-18).
   *
   * Every other surface that carries them has a footer to carry them in — the
   * public pages, the sign-in screen, the consent beside a card. The console
   * has none: it is one screen for the whole session, and a reader signed into
   * it had nowhere at all to read what they had agreed to. They belong on the
   * one line that is already about this account and already the quietest row on
   * the panel, so the sidebar asks for them and nothing else does — the money
   * step prints its own pair beside the consent (`perk.tsx`), and a second pair
   * two lines under it would be noise.
   */
  legal?: boolean;
  className?: string;
}) {
  const { leave, leaving } = useLeave();
  return (
    <p className={`text-[11px] text-fainter ${className}`}>
      {`Signed in as ${whoami(me)}. `}
      <button
        type="button"
        disabled={leaving}
        onClick={() => {
          leave();
          if (handle) saveKnockTarget(handle);
        }}
        className="underline underline-offset-2 transition-colors duration-75 hover:text-muted
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70
                   disabled:opacity-60"
      >
        {WRONG_ACCOUNT_LINK}
      </button>
      {legal && (
        /* The pair travels as one. A fifteen-character handle wraps this row in
           a narrow panel, and a reader who found "Terms" on one line and
           "Privacy" on the next was reading two orphans rather than a footer. */
        <span className="whitespace-nowrap">
          <a
            href={`${API_URL}/terms`}
            target="_blank"
            rel="noreferrer"
            className="ml-2 underline underline-offset-2 transition-colors duration-75 hover:text-muted
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
          >
            Terms
          </a>
          <a
            href={`${API_URL}/privacy`}
            target="_blank"
            rel="noreferrer"
            className="ml-2 underline underline-offset-2 transition-colors duration-75 hover:text-muted
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
          >
            Privacy
          </a>
          {/* About travels with the pair (wave 88). */}
          <AboutLink
            className="ml-2 underline underline-offset-2 transition-colors duration-75 hover:text-muted
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
          />
        </span>
      )}
    </p>
  );
}
